import { Injectable, isDevMode } from "@angular/core";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { from, lastValueFrom } from "rxjs";
import { User } from "../models/user";
import {
	getTierForFeature,
	hasTierAtLeast,
	SubscriptionFeature,
	SubscriptionStatus,
	SubscriptionTier,
} from "../models/entitlement";
import { AnalyticsService } from "./analytics.service";

export type CentralEventTabEntitlementKey =
	| "preliminaryResults"
	| "lapTimes"
	| "liveStream"
	| "videoClips";

export interface CentralEventTabEntitlements {
	loading: boolean;
	canViewPreliminaryResults: boolean;
	canViewLapTimes: boolean;
	canViewLiveStream: boolean;
	canViewVideoClips: boolean;
	reasons: Record<CentralEventTabEntitlementKey, string[]>;
}

const ACCESS_STATUSES: Array<SubscriptionStatus | ""> = [
	"active",
	"gracePeriod",
	"billingRetry",
];
const CURRENT_SUBSCRIPTION_MIGRATION_VERSION = 1;

@Injectable({
	providedIn: "root",
})
export class EntitlementService {
	private loggedCentralDebugKeys = new Set<string>();
	private loggedLegacyFallbackUsers = new Set<string>();

	constructor(
		private fns: Functions,
		private analytics: AnalyticsService
	) {}

	getRequiredTierForPath(path: string): SubscriptionTier {
		switch (path) {
			case "lap-times":
				return "bronze";
			case "live-stream":
				return "silver";
			case "video-clips":
				return "gold";
			default:
				return "startingGate";
		}
	}

	getRequiredFeatureForPath(path: string): SubscriptionFeature | null {
		switch (path) {
			case "lap-times":
				return "lapTimes";
			case "live-stream":
				return "liveStream";
			case "video-clips":
				return "videoClips";
			default:
				return null;
		}
	}

	hasFeature(user: User, feature: SubscriptionFeature): boolean {
		if (!this.hasActiveSummary(user)) return false;
		if ((user.currentEntitlements || []).includes(feature)) return true;
		return hasTierAtLeast(user.currentTier, getTierForFeature(feature));
	}

	hasTierAtLeast(user: User, requiredTier: SubscriptionTier): boolean {
		if (requiredTier === "startingGate") return true;
		return this.hasActiveSummary(user) && hasTierAtLeast(user.currentTier, requiredTier);
	}

	canAccessPath(user: User, path: string): boolean {
		const requiredTier = this.getRequiredTierForPath(path);
		return this.hasTierAtLeast(user, requiredTier);
	}

	canAccessCentralPath(user: User, path: string): boolean {
		const entitlements = this.getCentralEventTabEntitlements(user, path);
		switch (path) {
			case "lap-times":
				return (
					entitlements.canViewLapTimes ||
					entitlements.canViewPreliminaryResults
				);
			case "live-stream":
				return entitlements.canViewLiveStream;
			case "video-clips":
				return entitlements.canViewVideoClips;
			default:
				return this.canAccessPath(user, path);
		}
	}

	getCentralEventTabEntitlements(
		user: User,
		debugRoute = ""
	): CentralEventTabEntitlements {
		const entitlements: CentralEventTabEntitlements = {
			loading: !user || !user.uid,
			canViewPreliminaryResults: false,
			canViewLapTimes: false,
			canViewLiveStream: false,
			canViewVideoClips: false,
			reasons: {
				preliminaryResults: [],
				lapTimes: [],
				liveStream: [],
				videoClips: [],
			},
		};

		if (!user || !user.uid) {
			this.debugCentralEventEntitlements(user, entitlements, debugRoute);
			return entitlements;
		}

		this.applyUserFeatureGrant(user, "lapTimes", entitlements);
		this.applyUserFeatureGrant(user, "liveStream", entitlements);
		this.applyUserFeatureGrant(user, "videoClips", entitlements);

		if (this.hasInternalAdminAccess(user)) {
			this.grantAllCentralEventTabs(entitlements, "internalAdminRole");
		}

		if (this.hasTrackOwnerSubscriptionAccess(user)) {
			this.grantAllCentralEventTabs(
				entitlements,
				"activeTrackOwnerSubscription"
			);
		}

		this.debugCentralEventEntitlements(user, entitlements, debugRoute);
		return entitlements;
	}

	hasActiveSummary(user: User): boolean {
		if (!user) return false;
		if (!ACCESS_STATUSES.includes(user.subscriptionStatus || "")) return false;
		const expirationValue: any = user.subscriptionExpiresAt;
		const expiration = expirationValue?.toDate
			? expirationValue.toDate()
			: expirationValue
				? new Date(expirationValue)
				: null;
		if (expiration && !Number.isNaN(expiration.getTime()) && expiration.getTime() <= Date.now()) return false;
		return hasTierAtLeast(user.currentTier || "startingGate", "bronze");
	}

	async refreshCurrentUserEntitlements(): Promise<void> {
		const callable = httpsCallable(this.fns, "refreshUserEntitlements", {
			timeout: 60000,
		});
		await lastValueFrom(from(callable({})));
	}

	async startRevenueCatChange(targetTier: Exclude<SubscriptionTier, "startingGate">, targetProductId: string): Promise<{ requestId: string }> {
		const callable = httpsCallable<any, { requestId: string }>(this.fns, "startRiderSubscriptionChange", { timeout: 60000 });
		return (await lastValueFrom(from(callable({ provider: "revenueCat", targetTier, targetProductId })))).data;
	}

	async completeRevenueCatChange(requestId: string, providerTransactionId: string): Promise<void> {
		const callable = httpsCallable(this.fns, "completeRiderSubscriptionChange", { timeout: 60000 });
		await lastValueFrom(from(callable({ requestId, providerTransactionId })));
	}

	async failRevenueCatChange(requestId: string): Promise<void> {
		const callable = httpsCallable(this.fns, "failRiderSubscriptionChange", { timeout: 60000 });
		await lastValueFrom(from(callable({ requestId })));
	}

	private applyUserFeatureGrant(
		user: User,
		feature: SubscriptionFeature,
		entitlements: CentralEventTabEntitlements
	) {
		if (!this.hasActiveUserSubscriptionSignal(user)) return;

		const hasExplicitFeature = (user.currentEntitlements || []).includes(feature);
		const hasTierFeature = hasTierAtLeast(
			user.currentTier || "startingGate",
			getTierForFeature(feature)
		);
		if (!hasExplicitFeature && !hasTierFeature) return;

		const reason = hasExplicitFeature
			? `userEntitlement:${feature}`
			: `userTier:${user.currentTier || "startingGate"}`;
		this.grantCentralEventFeature(feature, entitlements, reason);
	}

	private grantCentralEventFeature(
		feature: SubscriptionFeature,
		entitlements: CentralEventTabEntitlements,
		reason: string
	) {
		switch (feature) {
			case "lapTimes":
				entitlements.canViewPreliminaryResults = true;
				entitlements.canViewLapTimes = true;
				this.addReason(entitlements, "preliminaryResults", reason);
				this.addReason(entitlements, "lapTimes", reason);
				break;
			case "liveStream":
				entitlements.canViewLiveStream = true;
				this.addReason(entitlements, "liveStream", reason);
				break;
			case "videoClips":
				entitlements.canViewVideoClips = true;
				this.addReason(entitlements, "videoClips", reason);
				break;
		}
	}

	private grantAllCentralEventTabs(
		entitlements: CentralEventTabEntitlements,
		reason: string
	) {
		entitlements.canViewPreliminaryResults = true;
		entitlements.canViewLapTimes = true;
		entitlements.canViewLiveStream = true;
		entitlements.canViewVideoClips = true;
		this.addReason(entitlements, "preliminaryResults", reason);
		this.addReason(entitlements, "lapTimes", reason);
		this.addReason(entitlements, "liveStream", reason);
		this.addReason(entitlements, "videoClips", reason);
	}

	private addReason(
		entitlements: CentralEventTabEntitlements,
		key: CentralEventTabEntitlementKey,
		reason: string
	) {
		if (!entitlements.reasons[key].includes(reason)) {
			entitlements.reasons[key].push(reason);
		}
	}

	private hasActiveUserSubscriptionSignal(user: User): boolean {
		if (this.hasActiveSummary(user)) return true;
		const migration = user?.subscriptionMigration;
		const migrationComplete =
			migration?.status === "migrated" &&
			Number(migration?.version || 0) >= CURRENT_SUBSCRIPTION_MIGRATION_VERSION &&
			migration?.parityValidated === true &&
			Boolean(migration?.activatedAccessDigest);
		const usesLegacyFallback =
			user?.activeSubscription === true && !migrationComplete;
		if (usesLegacyFallback && user?.uid && !this.loggedLegacyFallbackUsers.has(user.uid)) {
			this.loggedLegacyFallbackUsers.add(user.uid);
			this.analytics.trigger("legacy_subscription_fallback_used", {
				migration_status: migration?.status || "notStarted",
			});
		}
		return usesLegacyFallback;
	}

	private hasInternalAdminAccess(user: User): boolean {
		return user?.role === "admin";
	}

	private hasTrackOwnerSubscriptionAccess(user: User): boolean {
		const hasOwnedTrackContext =
			user?.isTrackOwner === true || !!user?.flaggerInstanceId;
		const hasSelectedOrOwnedTrack =
			!!user?.flaggerInstanceId || !!user?.sponsoredTrackId;
		return (
			hasOwnedTrackContext &&
			hasSelectedOrOwnedTrack &&
			this.hasActiveUserSubscriptionSignal(user)
		);
	}

	private debugCentralEventEntitlements(
		user: User,
		entitlements: CentralEventTabEntitlements,
		route: string
	) {
		if (!route || !isDevMode()) return;

		const debugKey = [
			route,
			user?.uid || "no-user",
			user?.role || "",
			user?.currentTier || "",
			user?.subscriptionStatus || "",
			user?.activeSubscription ? "activeLegacy" : "inactiveLegacy",
			entitlements.canViewLapTimes,
			entitlements.canViewLiveStream,
			entitlements.canViewVideoClips,
		].join("|");
		if (this.loggedCentralDebugKeys.has(debugKey)) return;
		this.loggedCentralDebugKeys.add(debugKey);

		console.debug("Central event tab entitlements", {
			route,
			userUid: user?.uid || "",
			role: user?.role || "",
			isTrackOwner: user?.isTrackOwner === true,
			flaggerInstanceId: user?.flaggerInstanceId || "",
			sponsoredTrackId: user?.sponsoredTrackId || "",
			currentTier: user?.currentTier || "startingGate",
			subscriptionStatus: user?.subscriptionStatus || "",
			currentEntitlements: user?.currentEntitlements || [],
			activeSubscription: user?.activeSubscription === true,
			canViewPreliminaryResults:
				entitlements.canViewPreliminaryResults,
			canViewLapTimes: entitlements.canViewLapTimes,
			canViewLiveStream: entitlements.canViewLiveStream,
			canViewVideoClips: entitlements.canViewVideoClips,
			reasons: entitlements.reasons,
		});
	}
}
